import { Link, useLocation } from '@tanstack/react-router'
import {
  Home,
  Crown,
  CalendarDays,
  Route as RouteIcon,
  Megaphone,
  MessageSquare,
  Inbox,
  Users,
  ContactRound,
  FileText,
  Plane,
  NotebookText,
  TowerControl,
  ClipboardCheck,
  Settings,
  LogOut,
  type IconComponent,
} from '@/components/icons'
import { useAuth } from '@/hooks/useAuth'
import { useUnreadNotificationCount } from '@/lib/convex-hooks'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { useIsPilot } from '@/hooks/useIsPilot'
import { ProfileAvatarWithBadge } from '@/components/ProfileAvatarWithBadge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import pronteerLogo from '@/assets/pronteer-logo-full.svg'

interface NavItem {
  label: string
  to: string
  icon: IconComponent
  /** Matches the active route. Defaults to a startsWith on `to`. */
  isActive?: (pathname: string) => boolean
  /** Only shown to pilots (admins always see it) — mirrors the /flights route guard. */
  pilotOnly?: boolean
}

// Company-wide flights table; the route itself admits admins and pilots.
const FLIGHTS_NAV_ITEM: NavItem = {
  label: 'Flights',
  to: '/flights',
  icon: NotebookText,
  pilotOnly: true,
}

// Grouped to match the desktop design — Operations / comms, directory & records,
// then settings pinned near the bottom.
const ADMIN_NAV_GROUPS: NavItem[][] = [
  [
    {
      label: 'Home',
      to: '/home',
      icon: Home,
      // Exact match so it doesn't stay active on other routes.
      isActive: (p) => p === '/home',
    },
  ],
  [
    {
      label: 'My Schedule',
      to: '/schedule',
      icon: CalendarDays,
      isActive: (p) => p.startsWith('/schedule') || p.startsWith('/roster/'),
    },
  ],
  [
    {
      label: 'Operations',
      to: '/operations',
      icon: Crown,
      // Exact match so sub-routes (trips, rosters…) don't keep the hub active.
      isActive: (p) => p === '/operations',
    },
    { label: 'Schedule', to: '/operations/rosters', icon: CalendarDays },
    { label: 'Trips', to: '/operations/trips', icon: RouteIcon },
    { label: 'Announcements', to: '/operations/announcements', icon: Megaphone },
  ],
  [
    { label: 'Chat', to: '/conversations', icon: MessageSquare },
    { label: 'Inbox', to: '/inbox', icon: Inbox },
  ],
  [
    { label: 'Crew Directory', to: '/operations/crew-directory', icon: Users },
    { label: 'PAX Directory', to: '/operations/pax', icon: ContactRound },
    { label: 'Documents', to: '/operations/documents', icon: FileText },
    { label: 'Aircraft', to: '/operations/aircraft', icon: Plane },
    FLIGHTS_NAV_ITEM,
    { label: 'Airport Directory', to: '/airport-directory', icon: TowerControl },
    {
      label: 'Checklists',
      to: '/tasks',
      icon: ClipboardCheck,
      // The hub plus every checklist screen it links to (builder, start, archived).
      isActive: (p) =>
        p === '/tasks' ||
        p.startsWith('/tasks/') ||
        p.startsWith('/operations/checklisttemplates') ||
        p.startsWith('/operations/tasks'),
    },
  ],
  [{ label: 'Settings', to: '/operations/settings', icon: Settings }],
]

// Crew (non-admin) sidebar — mirrors the native app: their own schedule plus
// the crew-scoped directory routes (the same ones the mobile More menu uses).
const CREW_NAV_GROUPS: NavItem[][] = [
  [
    {
      label: 'Home',
      to: '/home',
      icon: Home,
      isActive: (p) => p === '/home',
    },
  ],
  [
    {
      label: 'My Schedule',
      to: '/schedule',
      icon: CalendarDays,
      isActive: (p) => p.startsWith('/schedule') || p.startsWith('/roster/'),
    },
  ],
  [
    { label: 'Chat', to: '/conversations', icon: MessageSquare },
    { label: 'Inbox', to: '/inbox', icon: Inbox },
  ],
  [
    { label: 'Crew Directory', to: '/crew-directory', icon: Users },
    { label: 'PAX Directory', to: '/pax', icon: ContactRound },
    { label: 'Documents', to: '/documents', icon: FileText },
    { label: 'Aircraft', to: '/aircraft', icon: Plane },
    FLIGHTS_NAV_ITEM,
    { label: 'Airport Directory', to: '/airport-directory', icon: TowerControl },
    {
      label: 'Checklists',
      to: '/tasks',
      icon: ClipboardCheck,
      isActive: (p) => p === '/tasks' || p.startsWith('/tasks/'),
    },
  ],
  [{ label: 'Settings', to: '/profile/settings', icon: Settings }],
]

function isItemActive(item: NavItem, pathname: string) {
  if (item.isActive) return item.isActive(pathname)
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

function getInitials(crew?: {
  firstName?: string | null
  lastName?: string | null
  name?: string | null
}) {
  if (!crew) return 'U'
  if (crew.firstName && crew.lastName) {
    return `${crew.firstName.charAt(0)}${crew.lastName.charAt(0)}`.toUpperCase()
  }
  if (crew.name) {
    const parts = crew.name.trim().split(' ')
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
    }
    return parts[0].charAt(0).toUpperCase()
  }
  return 'U'
}

function getDisplayName(crew?: {
  firstName?: string | null
  lastName?: string | null
  name?: string | null
}) {
  if (!crew) return 'Profile'
  if (crew.firstName && crew.lastName) return `${crew.firstName} ${crew.lastName}`
  if (crew.firstName) return crew.firstName
  if (crew.name) return crew.name
  return 'Profile'
}

interface SidebarCrew {
  firstName?: string | null
  lastName?: string | null
  name?: string | null
  avatarColor?: string
  photoS3Key?: string | null
}

function ProfileNavRow({ active }: { active: boolean }) {
  const { data, isPending } = useCurrentCrewMember()
  const currentUser = data as SidebarCrew | undefined
  const { isAdmin } = useIsAdmin()

  return (
    <Link
      to="/profile"
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-bold transition-colors',
        active
          ? 'bg-background-brand-subtle text-content-brand-bold'
          : 'text-content-primary-subtle hover:bg-surface-L1 hover:text-content-primary-bold',
      )}
    >
      {isPending ? (
        <>
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <Skeleton className="h-4 w-28" />
        </>
      ) : (
        <>
          <ProfileAvatarWithBadge
            fallbackText={getInitials(currentUser)}
            className="size-8 text-sm"
            photoS3Key={currentUser?.photoS3Key}
            avatarColor={currentUser?.avatarColor}
            isAdmin={isAdmin}
            useCurrentUser={false}
            badgeClassName="-top-1 -right-1 p-[3px] ring-2 ring-surface-L0"
            iconClassName="size-2.5"
          />
          <span className="truncate">{getDisplayName(currentUser)}</span>
        </>
      )}
    </Link>
  )
}

function NavRow({
  item,
  active,
  badgeCount,
}: {
  item: NavItem
  active: boolean
  badgeCount?: number
}) {
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-background-brand-subtle text-content-brand-bold'
          : 'text-content-primary-subtle hover:bg-surface-L1 hover:text-content-primary-bold',
      )}
    >
      <Icon className="size-[18px] shrink-0" />
      <span className="truncate">{item.label}</span>
      {badgeCount !== undefined && badgeCount > 0 && (
        <span className="ml-auto flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-content-negative-bold px-1 text-[10px] font-semibold text-white">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </Link>
  )
}

export function DesktopSidebar() {
  const location = useLocation()
  const { signOut } = useAuth()
  const pathname = location.pathname
  const { isAdmin } = useIsAdmin()
  const { isPilot } = useIsPilot()
  const { data: crew } = useCurrentCrewMember()
  const crewId = (crew as { id?: string } | undefined)?.id
  const unreadNotificationCount = useUnreadNotificationCount(crewId)

  // Crew excluded from scheduling don't get the My Schedule item (mirrors BottomNav).
  const includeInSchedule =
    (crew as { includeInSchedule?: boolean } | undefined)?.includeInSchedule !== false
  // Pilot-only items (Flights) drop out for non-pilot crew; admins keep them.
  const navGroups = (isAdmin ? ADMIN_NAV_GROUPS : CREW_NAV_GROUPS)
    .filter((group) => includeInSchedule || group[0]?.to !== '/schedule')
    .map((group) => group.filter((item) => !item.pilotOnly || isAdmin || isPilot))

  return (
    <aside className="relative z-30 m-3 flex h-[calc(100%-1.5rem)] w-[260px] shrink-0 flex-col overflow-hidden rounded-3xl border border-border-L1 bg-surface-L0 shadow-[0px_5px_30px_rgba(0,0,0,0.12)]">
      {/* Logo */}
      <div className="flex h-20 items-center px-6">
        <img src={pronteerLogo} alt="Pronteer" className="h-8 w-auto" />
      </div>

      {/* Profile */}
      <div className="border-y border-border-L1 px-3 py-2">
        <ProfileNavRow active={pathname.startsWith('/profile')} />
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {navGroups.map((group, groupIndex) => (
          <div
            key={groupIndex}
            className={cn(
              'flex flex-col gap-1',
              groupIndex > 0 && 'mt-2 border-t border-border-L1 pt-2',
            )}
          >
            {group.map((item) => (
              <NavRow
                key={item.to}
                item={item}
                active={isItemActive(item, pathname)}
                badgeCount={item.to === '/inbox' ? unreadNotificationCount : undefined}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Log out */}
      <div className="border-t border-border-L1 px-3 py-3">
        <button
          type="button"
          onClick={() => void signOut()}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-content-primary-subtle transition-colors hover:bg-surface-L1 hover:text-content-primary-bold"
        >
          <LogOut className="size-[18px] shrink-0" />
          <span>Log out</span>
        </button>
      </div>
    </aside>
  )
}
